import type { ReactNode, JSX, CSSProperties } from 'react'

import { ChannelIcon, Text, VisuallyHidden } from '@buffer-mono/popcorn'
import { isChannelType, normalizeNetworkName } from '~publish/helpers/channels'

import styles from './chart-shared.module.css'

/**
 * A channel as the All-Channels chart presents it: identity plus the line
 * colour assigned to it. Colour is bundled so the legend swatch, picker, and
 * chart line all agree (it's assigned once at the single source of truth).
 */
export interface ChannelLegendData {
  id: string
  name: string
  service: string
  color: string
}

/**
 * Neutral channel icon — the platform glyph without its brand colours (the
 * colour swatch beside it carries the channel's colour instead). `inverted` is
 * for dark surfaces (the "+N more" tooltip), where the subtle glyph would
 * vanish. Centralised so the `service` → `ChannelType` mapping lives in one place.
 */
export function channelIconElement(
  service: string,
  color: 'subtle' | 'inverted' = 'subtle',
): ReactNode {
  if (!isChannelType(service)) return null
  return <ChannelIcon type={service} color={color} size="xsmall" />
}

type ColorDotStyle = CSSProperties & { '--dot-color': string }

interface ChannelLegendItemProps {
  channel: ChannelLegendData
  /** Icon colour variant — see {@link channelIconElement}. Pass `inverted` when
   * rendered on a dark surface. */
  iconColor?: 'subtle' | 'inverted'
}

/**
 * A channel's legend/picker entry: a colour swatch, its (neutral) icon, and its
 * name. The swatch/icon/name are decorative; a single visually-hidden label
 * gives assistive tech one clean "<service> <channel name>" reading rather than
 * announcing the icon or a duplicated name.
 */
export function ChannelLegendItem({
  channel,
  iconColor = 'subtle',
}: ChannelLegendItemProps): JSX.Element {
  const dotStyle: ColorDotStyle = { '--dot-color': channel.color }

  return (
    <span className={styles.metricLabel}>
      <span
        className={`${styles.colorDot} ${styles.colorDotSquare}`}
        aria-hidden="true"
        style={dotStyle}
      />
      <span className={styles.metricLabelIcon} aria-hidden="true">
        {channelIconElement(channel.service, iconColor)}
      </span>
      <Text size="sm" aria-hidden="true">
        {channel.name}
      </Text>
      <VisuallyHidden>
        {normalizeNetworkName(channel.service)} {channel.name}
      </VisuallyHidden>
    </span>
  )
}
